/**
 * Suspicious v2 §4.2 — "Block the addresses this bot used" section on
 * the bot / agent detail (MandateDetailV3 Overview).
 *
 * Reads the attested addresses the server returns from GET
 * /v1/sites/:site_id/sender-addresses?agent_key=X (ADR-0246 posture:
 * owner / admin / reviewer at the site; viewer / support / acting
 * agency get `status: 'hidden'` and the section hides).
 *
 * Per-row Block (owner/admin only) + a "Block all N" aggregator that
 * confirms once and runs the per-row POSTs. For an impostor row the
 * copy says "Block the addresses using this name" (the merchant is
 * blocking NETWORK addresses, not the agent name itself; the
 * verified real crawler keeps working).
 *
 * The parent component picks whether to render this section at all:
 *   - verified crawler → render a one-line verified line, no section
 *   - impostor         → render the section with the "using this name"
 *                        subtitle, Hold/Refuse hidden
 *   - unverified /
 *     unknown          → render the section as-is
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { BlockingState, SenderAddress } from '@sill/types/api';

import { useOptionalHttp } from '../../api/http-context.js';
import type { HttpClient } from '../../api/http-client.js';
import {
  deleteBlocklistEntry,
  getBlocking,
  getBlocklistLookup,
  getSenderAddresses,
  postManualBlock,
  type BlockLookupHit,
} from '../../api/threats.js';
import { useC } from '../../lib/theme.js';
import { ConfirmDialog } from '../../components/atoms/index.js';
import { BlockUnavailableButton, blockUnavailableReason, useIsShopifySite } from './BlockControl.js';
import { TurnOnBlockingLink } from './TurnOnBlocking.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/**
 * Founder bug 2026-10-18 (block-state consistency): per-row chip
 * copy. Reads "Blocked until Oct 18" / "Blocked forever" / "Block
 * saved · not active" when blocking isn't delivering. Falls back to
 * plain "Blocked" when the lookup hasn't come back yet (optimistic
 * state from a POST that just succeeded).
 */
function rowChipLabel(hit: BlockLookupHit | undefined, blocking: BlockingState | null): string {
  const notDelivering = blocking !== null
    && (blocking.mode === 'off' || blocking.delivery === 'failing' || blocking.install !== 'worker_ready');
  if (notDelivering) return 'Block saved · not active';
  if (hit === undefined) return 'Blocked';
  if (hit.expires_at === null) return 'Blocked forever';
  try {
    const d = new Date(hit.expires_at);
    if (Number.isNaN(d.getTime())) return 'Blocked';
    return `Blocked until ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d)}`;
  } catch { return 'Blocked'; }
}

export type BotAddressBlockVariant = 'unverified' | 'impostor';

export interface BotAddressBlockSectionProps {
  readonly siteId: string;
  readonly agentKey: string;
  /** Owner / admin unlocks per-row + Block-all buttons; others see read-only. */
  readonly canMutate: boolean;
  /** Changes the subtitle wording ("...this bot used" vs "...using this name"). */
  readonly variant: BotAddressBlockVariant;
  /**
   * Suspicious v2 (founder 2026-10-18) — change counter the parent
   * increments when the Panel's top-right DetailActionRow fires
   * "Block ▾". Each tick runs the Block-all flow on this section.
   * Omit on surfaces that don't have a parent action row.
   */
  readonly externalBlockAllTick?: number | undefined;
  /**
   * Called once a parent Block request has been picked up (the confirm
   * dialog opened). The parent resets its counter so a later remount
   * never replays an old click.
   */
  readonly onExternalBlockAllHandled?: () => void;
  /**
   * Founder 2026-10-19 — the viewed record's own address. Bots with no
   * registered name (UA `Mozilla/5.0`, 718 such user agents a week on
   * prod) carry a UA-hash agent_key that matches no crawl event, so the
   * list comes back empty. The top-right Block then blocks this address
   * instead, with no list shown (the IP sits in the Location row).
   */
  readonly fallbackAddress?: string | null | undefined;
}

type Load =
  | { readonly kind: 'loading' }
  | { readonly kind: 'hidden' }
  | { readonly kind: 'empty' }
  | { readonly kind: 'error' }
  | { readonly kind: 'ready'; readonly addresses: readonly SenderAddress[]; readonly unverified_addresses: number };

export function BotAddressBlockSection(props: BotAddressBlockSectionProps): JSX.Element | null {
  const C = useC();
  const isShopify = useIsShopifySite(props.siteId);
  const http = useOptionalHttp();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [blocking, setBlocking] = useState<BlockingState | null>(null);
  const [blockedIps, setBlockedIps] = useState<ReadonlySet<string>>(() => new Set());
  // Founder bug 2026-10-18 (block-state consistency): per-row state
  // from the server-side blocklist/lookup, so a row that was already
  // blocked (e.g. covered by a /64) shows "Blocked until …" + Unblock
  // instead of a redundant Block button. Keyed by ip; CIDR-aware
  // match is handled server-side.
  const [addressBlocks, setAddressBlocks] = useState<ReadonlyMap<string, BlockLookupHit>>(() => new Map());
  const [unblockingIp, setUnblockingIp] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [confirmingBlockAll, setConfirmingBlockAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Founder 2026-10-19 — empty list + the record's own address: Block
  // targets that one address and the list itself stays hidden.
  const [fallbackMode, setFallbackMode] = useState(false);

  useEffect(() => {
    if (http === null) { setLoad({ kind: 'error' }); return; }
    setLoad({ kind: 'loading' });
    setFallbackMode(false);
    let live = true;
    void getSenderAddresses(http, props.siteId, props.agentKey)
      .then((res) => {
        if (!live) return;
        if (res.status === 'hidden') setLoad({ kind: 'hidden' });
        else if (res.addresses.length === 0) setLoad({ kind: 'empty' });
        else setLoad({ kind: 'ready', addresses: res.addresses, unverified_addresses: res.summary?.unverified_addresses ?? res.addresses.length });
      })
      .catch(() => { if (live) setLoad({ kind: 'error' }); });
    return () => { live = false; };
  }, [http, props.siteId, props.agentKey]);

  const { fallbackAddress } = props;
  useEffect(() => {
    if (load.kind !== 'empty' || fallbackAddress == null) return;
    setFallbackMode(true);
    setLoad({
      kind: 'ready',
      addresses: [{
        address: fallbackAddress,
        requests: 1,
        last_seen: '',
        example_paths: [],
        identity_verification: 'unverified',
        identity_verification_label: null,
      }],
      unverified_addresses: 1,
    });
  }, [load.kind, fallbackAddress]);

  useEffect(() => {
    if (http === null || !props.canMutate) return;
    void getBlocking(http, props.siteId)
      .then((r) => { if (r != null && r.blocking != null) setBlocking(r.blocking); })
      .catch(() => undefined);
  }, [http, props.siteId, props.canMutate]);

  // Founder bug 2026-10-18 (block-state consistency): refresh per-row
  // block state from the server after any mutation, so Block / Unblock
  // flip the row immediately without a reload.
  const refreshAddressBlocks = useCallback(async () => {
    if (http === null || load.kind !== 'ready') return;
    const ips = load.addresses.map((a) => a.address).slice(0, 32);
    try {
      const { lookups } = await getBlocklistLookup(http, props.siteId, ips);
      const next = new Map<string, BlockLookupHit>();
      for (const l of lookups) if (l.blocked) next.set(l.ip, l);
      setAddressBlocks(next);
      setBlockedIps(new Set(next.keys()));
    } catch { /* best-effort */ }
  }, [http, props.siteId, load]);
  useEffect(() => { void refreshAddressBlocks(); }, [refreshAddressBlocks]);

  // Returns `true` on success, `false` on a generic rejection, or the
  // response's `error` code string so the caller can map it to the
  // right copy (e.g. `cdn_or_proxy_range`, `cloud_ranges_loading`).
  const blockOne = useCallback(async (
    ip: string,
    http_c: HttpClient,
  ): Promise<true | false | string | { readonly error: 'plan_feature_required'; readonly message: string }> => {
    const res = await postManualBlock(http_c, props.siteId, {
      target: { kind: 'ip', value: ip },
      duration: '7d',
      source: { kind: 'scanner', ref: `agent_${props.agentKey}` },
    });
    if (res.ok) {
      setBlockedIps((prev) => { const next = new Set(prev); next.add(ip); return next; });
      return true;
    }
    if (res.error === 'self_block_confirm_required') {
      const retry = await postManualBlock(http_c, props.siteId, {
        target: { kind: 'ip', value: ip },
        duration: '7d',
        source: { kind: 'scanner', ref: `agent_${props.agentKey}` },
        confirm_self: true,
      });
      if (retry.ok) {
        setBlockedIps((prev) => { const next = new Set(prev); next.add(ip); return next; });
        return true;
      }
      return false;
    }
    if (res.error === 'plan_feature_required') {
      return {
        error: 'plan_feature_required',
        message: res.plan_message ?? (res.plan_name !== null && res.plan_name !== undefined
          ? `Blocking is part of the ${res.plan_name} plan.`
          : "Blocking is not on this site's plan."),
      };
    }
    return res.error;
  }, [props.agentKey, props.siteId]);

  const onBlockRow = useCallback(async (ip: string) => {
    if (http === null) return;
    setPending(ip);
    setError(null);
    try {
      const result = await blockOne(ip, http);
      if (result === true) await refreshAddressBlocks();
      else if (result === false) setError('That block was rejected.');
      else if (typeof result === 'object' && result.error === 'plan_feature_required') {
        setError(result.message);
      }
      else if (result === 'cdn_or_proxy_range') {
        setError("That address belongs to Cloudflare or a privacy relay used by many real visitors, so Sill won't block it.");
      } else if (result === 'cloud_ranges_loading') {
        setError('Sill is still loading network data. Try again in a few seconds.');
      } else setError('That block was rejected.');
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setPending(null);
    }
  }, [http, blockOne]);

  const onUnblockRow = useCallback(async (ip: string) => {
    if (http === null) return;
    const hit = addressBlocks.get(ip);
    if (hit === undefined) return;
    setUnblockingIp(ip);
    setError(null);
    try {
      const r = await deleteBlocklistEntry(http, props.siteId, hit.block_id);
      if (!r.ok) setError('That unblock was rejected.');
      await refreshAddressBlocks();
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setUnblockingIp(null);
    }
  }, [http, props.siteId, addressBlocks, refreshAddressBlocks]);

  const onBlockAll = useCallback(async () => {
    if (http === null || load.kind !== 'ready') return;
    setPending('__all__');
    setError(null);
    setConfirmingBlockAll(false);
    try {
      let failures = 0;
      // 2026-10-18 — Block-all skips verified addresses (the operator's
      // real crawler keeps working). The count below reflects the
      // unverified subset the loop actually attempted.
      const targets = load.addresses.filter((a) => a.identity_verification !== 'signature'
        && a.identity_verification !== 'published_ip'
        && a.identity_verification !== 'rdns');
      let cdn_or_proxy = 0;
      let loading = 0;
      // ADR-0263 S4 (QA review 2026-11-02 Finding 5) — if the gate
      // denies the FIRST attempt, every subsequent attempt would deny
      // the same way; stop the loop and surface the plan-copy message
      // instead of counting it as a generic "rejected".
      let plan_feature_message: string | null = null;
      for (const a of targets) {
        if (blockedIps.has(a.address)) continue;
        try {
          const result = await blockOne(a.address, http);
          if (result === true) continue;
          if (result === 'cdn_or_proxy_range') { cdn_or_proxy += 1; continue; }
          if (result === 'cloud_ranges_loading') { loading += 1; continue; }
          if (typeof result === 'object' && result !== null && result.error === 'plan_feature_required') {
            plan_feature_message = result.message;
            break;
          }
          failures += 1;
        } catch {
          failures += 1;
        }
      }
      if (plan_feature_message !== null) {
        setError(plan_feature_message);
      } else if (loading > 0) {
        setError('Sill is still loading network data. Try again in a few seconds.');
      } else if (cdn_or_proxy > 0 && failures === 0) {
        setError(`${cdn_or_proxy} of ${targets.length} addresses belong to Cloudflare or a privacy relay used by many real visitors, so Sill won't block them.`);
      } else if (failures + cdn_or_proxy > 0) {
        setError(`${failures + cdn_or_proxy} of ${targets.length} addresses were rejected.`);
      }
    } finally {
      setPending(null);
    }
  }, [http, load, blockedIps, blockOne]);

  // Suspicious v2 (founder 2026-10-18) — the "Block all" primary and
  // status line moved to the top-right DetailActionRow. This section
  // keeps a compact address list only, so the subtitle only appears
  // on the impostor variant (where the merchant has to see WHY these
  // addresses are listed under a bot name they don't own).
  const subtitle = useMemo(() => (props.variant === 'impostor'
    ? 'These addresses used this bot name.'
    : null), [props.variant]);

  // Suspicious v2 (founder 2026-10-18) — react to the parent's
  // top-right DetailActionRow "Block ▾" by running Block-all once per
  // tick. Starts at 0, so the ref guards against double-fire on
  // first mount (we want tick-driven, not mount-driven).
  // Founder 2026-10-19: the parent's Block click was never wired here,
  // and it must always confirm first (a name can be used by thousands of
  // real people's addresses). A pending request (tick > 0) opens the
  // confirm once the address list is ready; the parent then resets it.
  const { onExternalBlockAllHandled } = props;
  useEffect(() => {
    const tick = props.externalBlockAllTick ?? 0;
    if (tick === 0 || load.kind !== 'ready') return;
    onExternalBlockAllHandled?.();
    if (!props.canMutate) return;
    setConfirmingBlockAll(true);
  }, [props.externalBlockAllTick, props.canMutate, load.kind, onExternalBlockAllHandled]);

  if (fallbackMode) {
    return (
      <>
        {error !== null ? (
          <div data-testid="bot-address-block-error" style={{ marginBottom: 10, fontFamily: sansFont, fontSize: 12, color: C.coralFg }}>
            {error}
          </div>
        ) : null}
        <ConfirmDialog
          open={confirmingBlockAll}
          onClose={() => setConfirmingBlockAll(false)}
          title="Block this address for 7 days?"
          confirmLabel="Block address"
          cancelLabel="Don't block"
          confirmTone="coral"
          onConfirm={() => { setConfirmingBlockAll(false); void onBlockAll(); }}
          body={(
            <p data-testid="bot-address-block-one-confirm" style={{ fontFamily: sansFont, fontSize: 13, color: C.text, margin: 0 }}>
              Sill will block {props.fallbackAddress}, the address this visit came from.
              Cloudflare and privacy relays are never blocked.
            </p>
          )}
        />
      </>
    );
  }
  if (load.kind === 'loading' || load.kind === 'hidden' || load.kind === 'empty' || load.kind === 'error') {
    return null;
  }

  const unblockedCount = load.addresses.filter((a) => !blockedIps.has(a.address)).length;
  const blockAllCount = load.addresses.filter((a) => a.identity_verification !== 'signature'
    && a.identity_verification !== 'published_ip'
    && a.identity_verification !== 'rdns'
    && !blockedIps.has(a.address)).length;
  const totalUnverified = load.unverified_addresses;

  return (
    <section data-testid="bot-address-block" style={{ marginBottom: 16 }}>
      <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: C.muted, marginBottom: 6 }}>
        ADDRESSES USED
      </div>
      {subtitle !== null ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body, marginBottom: 10 }}>
          {subtitle}
        </div>
      ) : null}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {load.addresses.map((a) => {
          const already = blockedIps.has(a.address);
          const row_pending = pending === a.address;
          // 2026-10-18 — per-address verification. The Verified mark
          // replaces the Block button (the operator's real crawler keeps
          // working, so Block is never offered on a verified row).
          const verified = a.identity_verification === 'signature'
            || a.identity_verification === 'published_ip'
            || a.identity_verification === 'rdns';
          return (
            <li
              key={a.address}
              data-testid="bot-address-row"
              data-ip={a.address}
              data-verification={a.identity_verification}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                gap: 10,
                alignItems: 'center',
                padding: '6px 0',
                borderBottom: `1px solid ${C.borderSoft}`,
              }}
            >
              <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, overflowWrap: 'anywhere' }}>
                {a.address}
              </span>
              <span style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, whiteSpace: 'nowrap' }}>
                {a.requests.toLocaleString('en-US')} req
                {a.requests === 1 ? '' : 's'}
              </span>
              {verified ? (
                <span
                  data-testid="bot-address-verified"
                  // title-ok: full verification method sentence next to the badge.
                  title={a.identity_verification_label ?? 'Verified'}
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    letterSpacing: 1.5,
                    color: C.mintFg,
                    whiteSpace: 'nowrap',
                  }}
                >
                  VERIFIED
                </span>
              ) : props.canMutate ? (
                already ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                    <span
                      data-testid="bot-address-blocked"
                      style={{
                        fontFamily: sansFont,
                        fontSize: 11,
                        padding: '3px 7px',
                        border: `1px solid ${C.coralFg}`,
                        color: C.coralFg,
                        borderRadius: 2,
                      }}
                    >
                      {rowChipLabel(addressBlocks.get(a.address), blocking)}
                    </span>
                    <button
                      type="button"
                      data-testid="bot-address-unblock-row"
                      onClick={() => void onUnblockRow(a.address)}
                      disabled={unblockingIp === a.address}
                      style={{
                        fontFamily: sansFont,
                        fontSize: 12,
                        padding: '4px 10px',
                        background: 'transparent',
                        color: C.text,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                        cursor: unblockingIp === a.address ? 'wait' : 'pointer',
                        opacity: unblockingIp === a.address ? 0.6 : 1,
                      }}
                    >
                      {unblockingIp === a.address ? 'Unblocking…' : 'Unblock'}
                    </button>
                  </span>
                ) : blockUnavailableReason(blocking, { shopify: isShopify }) !== null ? (
                  <BlockUnavailableButton reason={blockUnavailableReason(blocking, { shopify: isShopify })!} testId="bot-address-block-row-disabled" />
                ) : (
                  <button
                    type="button"
                    data-testid="bot-address-block-row"
                    onClick={() => void onBlockRow(a.address)}
                    disabled={row_pending || pending !== null}
                    style={{
                      fontFamily: sansFont,
                      fontSize: 12,
                      padding: '4px 10px',
                      background: 'transparent',
                      color: C.text,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      cursor: row_pending ? 'wait' : 'pointer',
                    }}
                  >
                    {row_pending ? 'Blocking…' : 'Block'}
                  </button>
                )
              ) : null}
            </li>
          );
        })}
      </ul>
      {/* Suspicious v2 (founder 2026-10-18) — Block all / status
          line moved into the panel's top-right DetailActionRow. This
          section now shows the ADDRESSES LIST only (per-row Block
          still works for picking individual IPs). */}
      {error !== null ? (
        <div data-testid="bot-address-block-error" style={{ marginTop: 6, fontFamily: sansFont, fontSize: 12, color: C.coralFg }}>
          {error}
        </div>
      ) : null}
      <ConfirmDialog
        open={confirmingBlockAll}
        onClose={() => setConfirmingBlockAll(false)}
        title="Block these addresses for 7 days?"
        confirmLabel={`Block ${blockAllCount} address${blockAllCount === 1 ? '' : 'es'}`}
        cancelLabel="Don't block"
        confirmTone="coral"
        onConfirm={() => { setConfirmingBlockAll(false); void onBlockAll(); }}
        body={(
          <div data-testid="bot-address-block-all-confirm">
            <p style={{ fontFamily: sansFont, fontSize: 13, color: C.text, margin: '0 0 8px' }}>
              Sill will block the {blockAllCount} most active unverified address{blockAllCount === 1 ? '' : 'es'} used
              by this name{totalUnverified > blockAllCount ? ` (out of ${totalUnverified.toLocaleString('en-US')})` : ''}.
              Verified addresses, Cloudflare and privacy relays are never blocked.
            </p>
            {totalUnverified > MANY_ADDRESSES ? (
              <p data-testid="bot-address-block-all-many" style={{ fontFamily: sansFont, fontSize: 12, color: C.amberFg, margin: 0 }}>
                This name is used by {totalUnverified.toLocaleString('en-US')} addresses. That usually means real
                people (email apps, privacy relays, home networks), not one attacker. Blocking a few won’t stop
                it and may block real visitors.
              </p>
            ) : null}
          </div>
        )}
      />
    </section>
  );
}

/** Above this many distinct addresses a name is rarely one attacker. */
const MANY_ADDRESSES = 50;
